import { useState, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { useGroupedTools, useDeleteTool } from '../../hooks/useTools';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { Badge, Button, ConfirmModal, ControlledTabs, Input, ProviderLogo, getStatusVariant } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { getGroupLabel } from '../../utils/tool-groups';
import { deriveLogoSlug } from '../credentials/identity-lib';
import type { Tool, ToolGroup } from '../../types';
import { isPlatformEntity } from '../../types';
import GatewayList from './gateways/GatewayList';
import McpServerList from './mcp/McpServerList';

/**
 * Resolve the brand/product slug for a group: explicit group-level slug, else
 * the first tool that carries one, else the slugified label. Used for both the
 * group logo and for sorting the groups.
 */
function groupLogoSlug(group: ToolGroup): string {
  return (
    group.groupSlug ??
    group.tools.find((t) => t.groupSlug)?.groupSlug ??
    deriveLogoSlug(undefined, group.group)
  );
}

function CollapsibleGroup({
  group,
  groupSlug,
  tools,
  expanded,
  onToggle,
  headerRef,
  onToolClick,
  onDeleteClick,
  editHref,
}: {
  group: string;
  groupSlug?: string;
  tools: Tool[];
  /** Expanded state is owned by the parent so arrow-key nav can control it. */
  expanded: boolean;
  onToggle: () => void;
  headerRef?: (el: HTMLButtonElement | null) => void;
  onToolClick: (tool: Tool) => void;
  onDeleteClick: (tool: Tool) => void;
  editHref: (toolId: string) => string;
}) {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        ref={headerRef}
        type="button"
        role="treeitem"
        aria-expanded={expanded}
        onClick={onToggle}
        className="focus-ring flex items-center gap-2 w-full px-4 py-3 bg-muted/50 hover:bg-muted text-left transition-colors"
      >
        <svg
          className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        {groupSlug && (
          <ProviderLogo
            slug={groupSlug}
            displayName={getGroupLabel(group)}
            variant="icon"
            grayscale
            className="mx-1.5 h-5 w-5 rounded"
          />
        )}
        <span className="ml-1.5 font-medium text-foreground">{getGroupLabel(group)}</span>
        <span className="text-sm text-muted-foreground">({tools.length})</span>
      </button>
      {expanded && (
        <div className="divide-y divide-border">
          {tools.map((tool) => (
            <div
              key={tool.toolId}
              onClick={() => onToolClick(tool)}
              className="flex items-center justify-between pl-[86px] pr-4 py-3 hover:bg-muted/30 cursor-pointer transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-foreground truncate">{tool.name}</p>
                  <Badge variant={tool.type === 'platform' ? 'info' : 'warning'}>
                    {tool.type}
                  </Badge>
                  <Badge variant={getStatusVariant(tool.status)}>{tool.status}</Badge>
                  {tool.source === 'gateway' && (
                    <Badge variant="purple">{tool.gatewayName || 'Gateway'}</Badge>
                  )}
                </div>
                <p className="text-muted-foreground text-xs mt-0.5 line-clamp-1">
                  {tool.description}
                </p>
              </div>
              {!isPlatformEntity(tool) && tool.source !== 'gateway' && (
                <div className="flex items-center gap-2 ml-4 shrink-0">
                  <Link
                    to={editHref(tool.toolId)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button variant="ghost" size="sm">
                      Edit
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteClick(tool);
                    }}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const TABS = [
  { id: 'tools', label: 'Tools' },
  { id: 'gateways', label: 'MCP Gateways' },
  { id: 'mcp-servers', label: 'MCP Servers' },
];

export default function ToolList() {
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();
  const { data, isLoading } = useGroupedTools();
  const deleteTool = useDeleteTool();

  const location = useLocation();
  const activeTab = /\/tools\/gateways(?:\/|$)/.test(location.pathname)
    ? 'gateways'
    : /\/tools\/mcp(?:-servers|\/)/.test(location.pathname)
      ? 'mcp-servers'
      : 'tools';
  const setActiveTab = (tab: string) => {
    const paths: Record<string, string> = {
      gateways: routes.console.spaceToolsGateways(spaceId),
      'mcp-servers': routes.console.spaceToolsMcpServers(spaceId),
      tools: routes.console.spaceTools(spaceId),
    };
    navigate(paths[tab] || routes.console.spaceTools(spaceId), { replace: true });
  };
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    tool: Tool | null;
  }>({ isOpen: false, tool: null });

  // `Esc` focuses the tool search. Routed via react-hotkeys-hook so it shows
  // up in the Shift+? cheatsheet and inherits its built-in skip for
  // input/textarea/contentEditable targets.
  useHotkeys(
    'escape',
    (event) => {
      event.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    },
    {
      description: 'Focus tool search',
      metadata: { group: 'Tools' },
    },
  );
  // Which groups are expanded (lifted out of CollapsibleGroup so arrow-key nav
  // can control it). Groups start collapsed.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(),
  );
  const groupHeaderRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const setGroupExpanded = (key: string, open: boolean) =>
    setExpandedGroups((prev) => {
      if (prev.has(key) === open) return prev;
      const next = new Set(prev);
      open ? next.add(key) : next.delete(key);
      return next;
    });

  const filteredGroups: ToolGroup[] = useMemo(() => {
    const groups = data?.groups || [];
    const query = searchQuery.toLowerCase();
    const matched = !query
      ? groups
      : groups
          .map((g) => ({
            ...g,
            tools: g.tools.filter(
              (tool) =>
                tool.name.toLowerCase().includes(query) ||
                tool.description.toLowerCase().includes(query) ||
                tool.toolId.toLowerCase().includes(query)
            ),
          }))
          .filter((g) => g.tools.length > 0);
    // Sort by brand prefix (the slug segment before the first "-", e.g. "optr",
    // "google", "aws") so a brand's groups cluster together, then alphabetically
    // by label within the brand. Sorting by the raw slug instead would put the
    // bare brand ("optr" — Platform/Utility/Web) ahead of its products
    // ("optr-activity", …) since a string sorts before itself-plus-a-suffix.
    const brand = (g: ToolGroup) => groupLogoSlug(g).split('-')[0];
    return [...matched].sort(
      (a, b) =>
        brand(a).localeCompare(brand(b)) ||
        getGroupLabel(a.group).localeCompare(getGroupLabel(b.group)),
    );
  }, [data, searchQuery]);

  // Keyboard nav over the group list (roving focus on the header buttons):
  // ↑/↓ move between groups, →/← expand/collapse the focused group.
  const focusGroupHeader = (index: number) => {
    const count = filteredGroups.length;
    if (count === 0) return;
    const clamped = Math.max(0, Math.min(index, count - 1));
    groupHeaderRefs.current[clamped]?.focus();
  };
  const handleGroupsKeyDown = (e: React.KeyboardEvent) => {
    const els = groupHeaderRefs.current;
    const current = els.findIndex((b) => b === document.activeElement);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusGroupHeader(current < 0 ? 0 : current + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusGroupHeader(current < 0 ? 0 : current - 1);
        break;
      case 'ArrowRight':
        if (current >= 0) {
          e.preventDefault();
          setGroupExpanded(filteredGroups[current].group, true);
        }
        break;
      case 'ArrowLeft':
        if (current >= 0) {
          e.preventDefault();
          setGroupExpanded(filteredGroups[current].group, false);
        }
        break;
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.tool) return;

    try {
      await deleteTool.mutateAsync(deleteModal.tool.toolId);
      success('Tool deleted successfully');
      setDeleteModal({ isOpen: false, tool: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete tool');
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Tools</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Platform tools and MCP integrations for your agents
          </p>
        </div>
        {activeTab === 'tools' && (
          <Link to={routes.console.spaceToolNew(spaceId)}>
            <Button>Create Tool</Button>
          </Link>
        )}
      </div>

      {/* Tabs */}
      <ControlledTabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab}>
        {activeTab === 'tools' && (
          <div className="space-y-6">
            {/* Search */}
            <div className="flex gap-4">
              <div className="flex-1 max-w-md">
                <Input
                  ref={searchInputRef}
                  placeholder="Search tools..."
                  value={searchQuery}
                  onChange={(e: { target: { value: string } }) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Grouped tools */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : filteredGroups.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {searchQuery
                  ? 'No tools match your search.'
                  : 'No tools found. Create your first tool to get started.'}
              </div>
            ) : (
              <div
                className="space-y-4"
                role="tree"
                aria-label="Tool groups"
                onKeyDown={handleGroupsKeyDown}
              >
                {filteredGroups.map((group, index) => (
                  <CollapsibleGroup
                    key={group.group}
                    group={group.group}
                    groupSlug={groupLogoSlug(group)}
                    tools={group.tools}
                    expanded={expandedGroups.has(group.group)}
                    onToggle={() => toggleGroup(group.group)}
                    headerRef={(el) => {
                      groupHeaderRefs.current[index] = el;
                    }}
                    onToolClick={(tool) => navigate(routes.console.spaceTool(spaceId, tool.toolId))}
                    onDeleteClick={(tool) => setDeleteModal({ isOpen: true, tool })}
                    editHref={(toolId) => routes.console.spaceToolEdit(spaceId, toolId)}
                  />
                ))}
              </div>
            )}

            {/* Delete confirmation modal */}
            <ConfirmModal
              isOpen={deleteModal.isOpen}
              onClose={() => setDeleteModal({ isOpen: false, tool: null })}
              onConfirm={handleDelete}
              title="Delete Tool"
              message={`Are you sure you want to delete "${deleteModal.tool?.name}"? This action cannot be undone.`}
              confirmText="Delete"
              isLoading={deleteTool.isPending}
            />
          </div>
        )}

        {activeTab === 'gateways' && <GatewayList />}

        {activeTab === 'mcp-servers' && <McpServerList />}
      </ControlledTabs>
    </div>
  );
}
